import { Link, useLocation } from 'react-router-dom';
import { Home, MessageCircle, BookOpen, Brain, Moon, Users, Sparkles, Shield, Crown, Settings, LogOut, Coins, Bell, Activity, Heart, Building2, Lightbulb, Stethoscope, UserCircle, Waves, UtensilsCrossed, Target, Compass, Dumbbell, Wind, TreePine, Gem, CircleDot, PenLine, ClipboardList, Calendar, Flame, TrendingDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { useUserRole } from '@/hooks/useUserRole';
import { usePersonalizedNav } from '@/hooks/usePersonalizedNav';
import { User } from '@supabase/supabase-js';
import { THERAPPI_AI_BRAND } from '@/lib/utils';

interface DesktopNavProps {
  user: User | null;
  onSignOut: () => void;
}

export const DesktopNav = ({ user, onSignOut }: DesktopNavProps) => {
  const location = useLocation();
  const { isAdmin, isPremium, isClinician, hasClinicianConnection } = useUserRole(user);
  const personalizedLinks = usePersonalizedNav(user);
  
  // Check if we're on a WHOOP demo page
  const isWhoopDemo = location.pathname.startsWith('/whoop');

  // Map personalised links to include icons
  const iconMap: Record<string, any> = {
    '/dashboard': Home,
    '/therapy': MessageCircle,
    '/daily-check-in': ClipboardList,
    '/sanctuary': TreePine,
    '/meditations': Brain,
    '/manifestation': Sparkles,
    '/telempathy-tools': Waves,
    '/sleep-and-dreams': Moon,
    '/future-self-navigator': Compass,
  };

  const mainLinks = [
    { path: '/dashboard', label: 'Dashboard', icon: Home },
    ...personalizedLinks.map(link => ({
      path: link.to,
      label: link.label,
      icon: iconMap[link.to] || MessageCircle
    }))
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <div className="hidden md:flex items-center gap-6 relative z-[70]">
      {/* Main Navigation Links */}
      <nav className="flex items-center gap-1">
        {mainLinks.map((link) => {
          const Icon = link.icon;
          const active = isActive(link.path);
          
          return (
            <Link
              key={link.path}
              to={link.path}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-md text-lg font-light transition-colors ${
                active
                  ? 'text-accent'
                  : 'text-foreground/80 hover:text-accent'
              }`}
            >
              <Icon className="h-5 w-5" />
              <span dangerouslySetInnerHTML={{ __html: link.label.replace(/®/g, '<sup class="text-[0.6em]">®</sup>').replace(/™/g, '<sup class="text-[0.6em]">™</sup>') }} />
            </Link>
          );
        })}
      </nav>

      {/* User Menu */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="gap-2 px-4 py-2.5 text-lg font-light hover:bg-muted">
            <div className="h-8 w-8 rounded-full bg-gradient-primary flex items-center justify-center">
              <span className="text-xs font-light text-primary-foreground">
                {user?.email?.charAt(0).toUpperCase()}
              </span>
            </div>
            <span className="hidden lg:inline text-lg font-light">{user?.email}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64 bg-background z-[100] max-h-[80vh] overflow-y-auto">
          <DropdownMenuLabel>
            <div className="flex flex-col gap-1">
              <p className="text-sm font-light leading-none">{user?.email}</p>
              <div className="flex flex-wrap gap-1 mt-1">
                {isAdmin && (
                  <Badge variant="default" className="text-xs">
                    <Crown className="h-3 w-3 mr-1" />
                    Admin
                  </Badge>
                )}
                {isClinician && (
                  <Badge variant="default" className="text-xs bg-emerald-600">
                    <Stethoscope className="h-3 w-3 mr-1" />
                    Clinician
                  </Badge>
                )}
                {isPremium && !isAdmin && (
                  <Badge variant="secondary" className="text-xs">Premium</Badge>
                )}
                {hasClinicianConnection && (
                  <Badge variant="outline" className="text-xs">
                    <UserCircle className="h-3 w-3 mr-1" />
                    Connected
                  </Badge>
                )}
              </div>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          
          {isWhoopDemo ? (
            <>
              {/* WHOOP Demo Menu Items - Non-navigable */}
              <DropdownMenuItem disabled className="opacity-60">
                <MessageCircle className="h-4 w-4 mr-2" />
                {THERAPPI_AI_BRAND}
              </DropdownMenuItem>
              <DropdownMenuItem disabled className="opacity-60">
                <TreePine className="h-4 w-4 mr-2" />
                The Sanctuary
              </DropdownMenuItem>
              <DropdownMenuItem disabled className="opacity-60">
                <ClipboardList className="h-4 w-4 mr-2" />
                Daily Check-In
              </DropdownMenuItem>
              <DropdownMenuItem disabled className="opacity-60">
                <Activity className="h-4 w-4 mr-2" />
                THERAPPI® x WHOOP
              </DropdownMenuItem>
            </>
          ) : (
            <>
              {/* 🧠 THERAPPI AI */}
              <DropdownMenuLabel className="text-[10px] text-muted-foreground tracking-wider font-medium">🧠 THERAPPI AI</DropdownMenuLabel>
              <DropdownMenuItem asChild>
                <Link to="/therapy" className="cursor-pointer">
                  <MessageCircle className="h-4 w-4 mr-2" />
                  {THERAPPI_AI_BRAND}
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/daily-check-in" className="cursor-pointer">
                  <ClipboardList className="h-4 w-4 mr-2" />
                  Daily Check-In
                </Link>
              </DropdownMenuItem>
              
              <DropdownMenuSeparator />

              {/* 📊 TRACK */}
              <DropdownMenuLabel className="text-[10px] text-muted-foreground tracking-wider font-medium">📊 TRACK</DropdownMenuLabel>
              <DropdownMenuItem asChild>
                <Link to="/nutrition" className="cursor-pointer">
                  <UtensilsCrossed className="h-4 w-4 mr-2" />
                  Food & Nutrition
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/sleep-and-dreams" className="cursor-pointer">
                  <Moon className="h-4 w-4 mr-2" />
                  Sleep & Dreams
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/exercise" className="cursor-pointer">
                  <Dumbbell className="h-4 w-4 mr-2" />
                  Exercise Log
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/journal" className="cursor-pointer">
                  <PenLine className="h-4 w-4 mr-2" />
                  Journal
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/lunar-cycle" className="cursor-pointer">
                  <CircleDot className="h-4 w-4 mr-2" />
                  Lunar & Cycle Tracking
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/whoop-dashboard" className="cursor-pointer">
                  <Activity className="h-4 w-4 mr-2" />
                  Therappi® x WHOOP®
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/goals" className="cursor-pointer">
                  <Target className="h-4 w-4 mr-2" />
                  Goals & Habits
                </Link>
              </DropdownMenuItem>

              <DropdownMenuSeparator />

              {/* 🌿 SANCTUARY */}
              <DropdownMenuLabel className="text-[10px] text-muted-foreground tracking-wider font-medium">🌿 SANCTUARY</DropdownMenuLabel>
              <DropdownMenuItem asChild>
                <Link to="/sanctuary" className="cursor-pointer">
                  <TreePine className="h-4 w-4 mr-2" />
                  The Sanctuary
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/meditation-library" className="cursor-pointer">
                  <Brain className="h-4 w-4 mr-2" />
                  Meditation Medication®
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/breathwork" className="cursor-pointer">
                  <Wind className="h-4 w-4 mr-2" />
                  Breathwork
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/rock-remedies" className="cursor-pointer">
                  <Gem className="h-4 w-4 mr-2" />
                  Rock Remedies™
                </Link>
              </DropdownMenuItem>

              <DropdownMenuSeparator />

              {/* ✨ GROW */}
              <DropdownMenuLabel className="text-[10px] text-muted-foreground tracking-wider font-medium">✨ GROW</DropdownMenuLabel>
              <DropdownMenuItem asChild>
                <Link to="/telempathy-tools" className="cursor-pointer">
                  <Waves className="h-4 w-4 mr-2" />
                  Telempathy Teachings®
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/manifestation" className="cursor-pointer">
                  <Sparkles className="h-4 w-4 mr-2" />
                  Manifestation
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/future-self-navigator" className="cursor-pointer">
                  <Compass className="h-4 w-4 mr-2" />
                  Future-Self Spotlight
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/course-library" className="cursor-pointer">
                  <BookOpen className="h-4 w-4 mr-2" />
                  Courses
                </Link>
              </DropdownMenuItem>

              <DropdownMenuSeparator />

              {/* 👥 CONNECT */}
              <DropdownMenuLabel className="text-[10px] text-muted-foreground tracking-wider font-medium">👥 CONNECT</DropdownMenuLabel>
              <DropdownMenuItem asChild>
                <Link to="/connect-and-grow" className="cursor-pointer">
                  <Users className="h-4 w-4 mr-2" />
                  Connect & Grow
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/relational" className="cursor-pointer">
                  <Heart className="h-4 w-4 mr-2" />
                  Relational Hub
                </Link>
              </DropdownMenuItem>

              <DropdownMenuSeparator />

              {/* More */}
              <DropdownMenuItem asChild>
                <Link to="/inflammation" className="cursor-pointer">
                  <Flame className="h-4 w-4 mr-2" />
                  Inflammation Hub
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/longevity" className="cursor-pointer">
                  <TrendingDown className="h-4 w-4 mr-2" />
                  Longevity Protocols
                </Link>
              </DropdownMenuItem>
              
              {isPremium && (
                <DropdownMenuItem asChild>
                  <Link to="/insights" className="cursor-pointer">
                    <Lightbulb className="h-4 w-4 mr-2" />
                    AI Insights
                  </Link>
                </DropdownMenuItem>
              )}

              <DropdownMenuItem asChild>
                <Link to="/crisis" className="cursor-pointer">
                  <Shield className="h-4 w-4 mr-2" />
                  Crisis Support
                </Link>
              </DropdownMenuItem>
            </>
          )}

          <DropdownMenuSeparator />

          {/* Clinician Portal */}
          {isClinician && (
            <>
              <DropdownMenuItem asChild>
                <Link to="/clinician" className="cursor-pointer">
                  <Stethoscope className="h-4 w-4 mr-2" />
                  Clinician Portal
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}

          {/* Client Portal (for users connected to clinicians) */}
          {hasClinicianConnection && (
            <>
              <DropdownMenuItem asChild>
                <Link to="/client-portal" className="cursor-pointer">
                  <UserCircle className="h-4 w-4 mr-2" />
                  My Clinician
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          
          {isAdmin && (
            <>
              <DropdownMenuItem asChild>
                <Link to="/admin" className="cursor-pointer">
                  <Settings className="h-4 w-4 mr-2" />
                  Admin Portal
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/business" className="cursor-pointer">
                  <Building2 className="h-4 w-4 mr-2" />
                  Business Operations
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          
          <DropdownMenuItem asChild>
            <Link to="/account" className="cursor-pointer">
              <Crown className="h-4 w-4 mr-2" />
              My Account
            </Link>
          </DropdownMenuItem>
          
          <DropdownMenuItem asChild>
            <Link to="/credits" className="cursor-pointer">
              <Coins className="h-4 w-4 mr-2" />
              Credits & AI Usage
            </Link>
          </DropdownMenuItem>

          <DropdownMenuItem asChild>
            <Link to="/settings" className="cursor-pointer">
              <Bell className="h-4 w-4 mr-2" />
              Notifications & Settings
            </Link>
          </DropdownMenuItem>
          
          <DropdownMenuSeparator />
          
          <DropdownMenuItem onClick={onSignOut} className="text-primary cursor-pointer hover:text-primary-light">
            <LogOut className="h-4 w-4 mr-2" />
            Sign Out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
